import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";

import {
	ButtonGroup,
	ButtonGroupItem
} from "src/common/components/ButtonGroup";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import { useValue } from "src/features/forms/hooks/useField";
import { MeasuredButtonGroupProps } from "src/features/forms/types";

enum YesNo {
	YES = "YES",
	NO = "NO"
}

const messages = defineMessages({
	notMeasured: {
		id: "form.two_choice.not_measured",
		defaultMessage: "Nem történt vizsgálat"
	}
});

interface MeasuredButtonGroupValue {
	measured: YesNo | undefined;
	result: string | undefined;
}

export const MeasuredButtonGroupRenderer: React.FunctionComponent<
	MeasuredButtonGroupProps
> = ({ options, ...directProps }) => {
	const isLocked = React.useContext(IsLockedContext);

	const [complexValue, onChangeComplexValue] = useValue<
		MeasuredButtonGroupValue | undefined
	>(undefined);

	const { measured, result } = complexValue || {};

	const value: string | undefined = React.useMemo(() => {
		if (measured === YesNo.YES) {
			return result;
		}
		if (measured === YesNo.NO) {
			return measured;
		}
		return undefined;
	}, [measured, result]);

	const onChangeValue = React.useCallback(
		(value: string | undefined) => {
			let measured: YesNo | undefined;
			let result: string | undefined;
			if (value === undefined) {
				measured = undefined;
				result = undefined;
			} else if (value === YesNo.NO) {
				measured = YesNo.NO;
				result = undefined;
			} else {
				measured = YesNo.YES;
				result = value;
			}
			onChangeComplexValue({ measured, result });
		},
		[onChangeComplexValue]
	);

	return (
		<ButtonGroup {...directProps} disabled={isLocked}>
			{options
				.concat({ value: YesNo.NO, label: messages.notMeasured })
				.map(option => (
					<ButtonGroupItem
						key={option.value}
						checked={option.value === value}
						value={option.value}
						onChangeValue={onChangeValue}
					>
						{option.label === undefined ? (
							value
						) : typeof option.label === "string" ? (
							option.label
						) : (
							<FormattedMessage {...option.label} />
						)}
					</ButtonGroupItem>
				))}
		</ButtonGroup>
	);
};
